/**
 * @module errors
 * @description Defines the typed error hierarchy and helpers shared across the
 * adapters package. Each error preserves domain-aware error codes so consumers
 * can discriminate failures without relying on string parsing while still
 * aligning with the shared `@geenius/errors` base contract.
 */

import { ErrorCode, GeeniusError } from "@geenius/errors";

// ─── Error Codes ────────────────────────────────────────────────────────────

/**
 * Error codes specific to database operations.
 * Used to categorize and handle different types of database failures.
 */
export type DbErrorCode =
  | "NOT_FOUND"
  | "CONSTRAINT_VIOLATION"
  | "QUERY_ERROR"
  | "CONNECTION_ERROR"
  | "COLLECTION_NOT_CONFIGURED";

/**
 * Error codes specific to authentication operations.
 * Covers credential validation, session management, and OAuth failures.
 */
export type AuthErrorCode =
  | "INVALID_CREDENTIALS"
  | "USER_EXISTS"
  | "SESSION_EXPIRED"
  | "UNAUTHORIZED"
  | "OAUTH_ERROR"
  | "SERVICE_ERROR";

/**
 * Error codes specific to AI/LLM operations.
 * Includes model-level errors, API limits, and token constraints.
 */
export type AiErrorCode =
  | "MODEL_ERROR"
  | "RATE_LIMIT"
  | "CONTEXT_LENGTH"
  | "SERVICE_ERROR"
  | "EMBEDDING_ERROR"
  | "INVALID_INPUT";

/**
 * Error codes specific to file storage operations.
 * Covers file lifecycle errors and quota management.
 */
export type StorageErrorCode =
  | "NOT_FOUND"
  | "UPLOAD_FAILED"
  | "DOWNLOAD_FAILED"
  | "PAYLOAD_TOO_LARGE"
  | "QUOTA_EXCEEDED"
  | "INVALID_KEY"
  | "SERVICE_ERROR";

/**
 * Error codes specific to payment/billing operations.
 * Covers subscription and checkout workflow errors.
 */
export type PaymentsErrorCode =
  | "INVALID_PLAN"
  | "CHECKOUT_FAILED"
  | "SUBSCRIPTION_NOT_FOUND"
  | "CANCEL_FAILED"
  | "SERVICE_ERROR"
  | "NOT_AVAILABLE";

/**
 * Error codes specific to admin panel operations.
 * Covers admin-level data and configuration errors.
 */
export type AdminErrorCode =
  | "USER_NOT_FOUND"
  | "OPERATION_FAILED"
  | "SERVICE_ERROR";

/**
 * Error codes for adapter configuration and runtime invariants.
 * These cover setup-time failures that happen before a domain-specific adapter
 * can be invoked safely.
 */
export type AdapterInfrastructureErrorCode =
  | "INVALID_CONFIG"
  | "MISSING_CONFIG"
  | "MISSING_RESOLVER"
  | "ADAPTER_NOT_CONFIGURED"
  | "MISSING_CONTEXT"
  | "INVARIANT_VIOLATION";

/**
 * Union of all adapter-specific error codes, including the shared Geenius
 * error codes that this package reuses from `@geenius/errors`.
 */
export type AdapterErrorCode =
  | ErrorCode
  | DbErrorCode
  | AuthErrorCode
  | AiErrorCode
  | StorageErrorCode
  | PaymentsErrorCode
  | AdminErrorCode
  | AdapterInfrastructureErrorCode
  | (string & {});

/**
 * Maps adapter-level error codes onto stable HTTP-like status codes so the
 * shared `GeeniusError` base always carries a concrete status value.
 *
 * @param code Adapter-specific error code to classify.
 * @returns A numeric status code aligned with the failure category.
 */
function getStatusCodeForAdapterError(code: AdapterErrorCode): number {
  switch (code) {
    case "NOT_FOUND":
    case "USER_NOT_FOUND":
    case "SUBSCRIPTION_NOT_FOUND":
    case "COLLECTION_NOT_CONFIGURED":
      return 404;
    case "INVALID_CREDENTIALS":
    case "UNAUTHORIZED":
    case "SESSION_EXPIRED":
      return 401;
    case "USER_EXISTS":
    case "CONSTRAINT_VIOLATION":
      return 409;
    case "RATE_LIMIT":
      return 429;
    case "PAYLOAD_TOO_LARGE":
      return 413;
    case "INVALID_INPUT":
    case "INVALID_KEY":
    case "INVALID_PLAN":
    case "CONTRACT_ERROR":
      return 400;
    default:
      return 500;
  }
}

function toBaseErrorCode(code: AdapterErrorCode): ErrorCode {
  switch (code) {
    case "NOT_FOUND":
    case "USER_NOT_FOUND":
    case "SUBSCRIPTION_NOT_FOUND":
    case "COLLECTION_NOT_CONFIGURED":
      return ErrorCode.NOT_FOUND;
    case "INVALID_CREDENTIALS":
    case "SESSION_EXPIRED":
    case "UNAUTHORIZED":
      return ErrorCode.UNAUTHORIZED;
    case "USER_EXISTS":
    case "CONSTRAINT_VIOLATION":
      return ErrorCode.CONFLICT;
    case "INVALID_CONFIG":
      return ErrorCode.INVALID_CONFIG;
    case "MISSING_CONFIG":
      return ErrorCode.MISSING_CONFIG;
    case "CONNECTION_ERROR":
      return ErrorCode.NETWORK_ERROR;
    case "QUERY_ERROR":
      return ErrorCode.DATABASE_ERROR;
    case "INVALID_INPUT":
    case "INVALID_KEY":
    case "INVALID_PLAN":
    case "PAYLOAD_TOO_LARGE":
    case "CONTRACT_ERROR":
      return ErrorCode.VALIDATION;
    default:
      return ErrorCode.INTERNAL;
  }
}

// ─── Base Error ─────────────────────────────────────────────────────────────

/**
 * Base error class for all adapter-related errors.
 * Provides structured error information for better error handling and logging.
 *
 * @example
 * try {
 *   await adapter.query(/* ... *\/);
 * } catch (error) {
 *   if (error instanceof AdapterError) {
 *     logger.error(`${error.domain}: ${error.code} - ${error.message}`);
 *     if (isRetryable(error)) {
 *       // Implement retry logic
 *     }
 *   }
 * }
 */
export class AdapterError extends GeeniusError {
  readonly adapterCode: AdapterErrorCode;
  readonly domain: string;

  /**
   * Creates a new AdapterError instance.
   *
   * @param message - Human-readable error message
   * @param domain - The adapter domain where the error occurred (db, auth, etc.)
   * @param code - Machine-readable error code for programmatic handling
   * @param cause - Optional underlying error that caused this error
   */
  constructor(
    message: string,
    domain: string,
    code: AdapterErrorCode,
    public readonly cause?: unknown,
  ) {
    super({
      message,
      code: toBaseErrorCode(code),
      cause,
      statusCode: getStatusCodeForAdapterError(code),
      context: {
        domain,
      },
    });
    Object.defineProperty(this, "code", {
      value: code,
      enumerable: true,
      configurable: true,
    });
    this.adapterCode = code;
    this.name = "AdapterError";
    this.domain = domain;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// ─── Domain Errors ──────────────────────────────────────────────────────────

/**
 * Error thrown by database adapter operations.
 * Specializes AdapterError with database-specific error codes.
 */
export class DbError extends AdapterError {
  /**
   * @param message - Description of what went wrong
   * @param code - Database-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(message: string, code: DbErrorCode, cause?: unknown) {
    super(message, "db", code, cause);
    this.name = "DbError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown by authentication adapter operations.
 * Specializes AdapterError with auth-specific error codes.
 */
export class AuthError extends AdapterError {
  /**
   * @param message - Description of what went wrong (e.g., credentials invalid)
   * @param code - Auth-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(message: string, code: AuthErrorCode, cause?: unknown) {
    super(message, "auth", code, cause);
    this.name = "AuthError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown by AI/LLM adapter operations.
 * Specializes AdapterError with AI-specific error codes.
 */
export class AiError extends AdapterError {
  /**
   * @param message - Description of what went wrong (e.g., rate limited)
   * @param code - AI-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(message: string, code: AiErrorCode, cause?: unknown) {
    super(message, "ai", code, cause);
    this.name = "AiError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown by file storage adapter operations.
 * Specializes AdapterError with storage-specific error codes.
 */
export class StorageError extends AdapterError {
  /**
   * @param message - Description of what went wrong (e.g., upload failed)
   * @param code - Storage-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(message: string, code: StorageErrorCode, cause?: unknown) {
    super(message, "storage", code, cause);
    this.name = "StorageError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown by payment adapter operations.
 * Specializes AdapterError with payment-specific error codes.
 */
export class PaymentsError extends AdapterError {
  /**
   * @param message - Description of what went wrong (e.g., checkout failed)
   * @param code - Payment-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(message: string, code: PaymentsErrorCode, cause?: unknown) {
    super(message, "payments", code, cause);
    this.name = "PaymentsError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown by admin adapter operations.
 * Specializes AdapterError with admin-specific error codes.
 */
export class AdminError extends AdapterError {
  /**
   * @param message - Description of what went wrong
   * @param code - Admin-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(message: string, code: AdminErrorCode, cause?: unknown) {
    super(message, "admin", code, cause);
    this.name = "AdminError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown when adapter configuration is invalid or unavailable.
 * Covers malformed config payloads and missing initialization.
 */
export class AdapterConfigurationError extends AdapterError {
  /**
   * @param message - Description of the configuration failure
   * @param code - Configuration-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(
    message: string,
    code: Extract<
      AdapterInfrastructureErrorCode,
      "INVALID_CONFIG" | "MISSING_CONFIG"
    >,
    cause?: unknown,
  ) {
    super(message, "config", code, cause);
    this.name = "AdapterConfigurationError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown when resolving an adapter through the registry fails.
 * Covers missing registrations and domains with no configured provider.
 */
export class AdapterResolutionError extends AdapterError {
  /**
   * @param message - Description of the resolution failure
   * @param code - Resolution-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(
    message: string,
    code: Extract<
      AdapterInfrastructureErrorCode,
      "MISSING_CONFIG" | "MISSING_RESOLVER"
    >,
    cause?: unknown,
  ) {
    super(message, "resolution", code, cause);
    this.name = "AdapterResolutionError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown when a UI adapter context is missing a provider or domain.
 * Used by React hooks and Solid primitives to fail fast with a typed error.
 */
export class AdapterContextError extends AdapterError {
  /**
   * @param message - Description of the missing context or adapter
   * @param code - Context-specific error code for routing and handling
   * @param cause - The underlying error when provided
   */
  constructor(
    message: string,
    code: Extract<
      AdapterInfrastructureErrorCode,
      "ADAPTER_NOT_CONFIGURED" | "MISSING_CONTEXT"
    >,
    cause?: unknown,
  ) {
    super(message, "context", code, cause);
    this.name = "AdapterContextError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Error thrown when the package encounters an unexpected runtime invariant.
 * Used for bootstrap and environment assumptions that cannot be recovered from.
 */
export class AdapterInvariantError extends AdapterError {
  /**
   * @param message - Description of the failed invariant
   * @param cause - The underlying error when provided
   */
  constructor(message: string, cause?: unknown) {
    super(message, "runtime", "INVARIANT_VIOLATION", cause);
    this.name = "AdapterInvariantError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Type guard to check if an error is an AdapterError.
 * Useful for error handling that needs to distinguish adapter errors from other error types.
 *
 * @param error - The error to check
 * @returns True if the error is an instance of AdapterError
 *
 * @example
 * catch (error) {
 *   if (isAdapterError(error)) {
 *     // Handle adapter-specific error
 *     logger.info(`Adapter error: ${error.code}`);
 *   }
 * }
 */
export function isAdapterError(error: unknown): error is AdapterError {
  return error instanceof AdapterError;
}

/**
 * Determines if an error is retryable based on its error code.
 * Some transient errors (rate limits, connection issues, service errors) should be retried,
 * while others (validation errors, not found) should not.
 *
 * @param error - The error to check
 * @returns True if the error is retryable, false otherwise
 *
 * @example
 * const maxRetries = 3;
 * for (let i = 0; i < maxRetries; i++) {
 *   try {
 *     return await adapter.operation();
 *   } catch (error) {
 *     if (isRetryable(error) && i < maxRetries - 1) {
 *       await delay(Math.pow(2, i) * 1000); // exponential backoff
 *       continue;
 *     }
 *     throw error;
 *   }
 * }
 */
export function isRetryable(error: unknown): boolean {
  if (!isAdapterError(error)) return false;
  // These error codes represent transient failures that typically resolve with retry
  const retryableCodes = new Set([
    "RATE_LIMIT",
    "CONNECTION_ERROR",
    "SERVICE_ERROR",
  ]);
  return retryableCodes.has(error.code);
}
